import { useEffect, useRef, useState } from 'react';

interface Props {
  onScan: (kod: string) => void;
  /** Kamerayı kapatma butonu; verilmezse buton gösterilmez. */
  onClose?: () => void;
}

/** Tarayıcının yerleşik barkod çözücüsü — Android Chrome ve Capacitor WebView'de var. */
interface BarcodeDetectorLike {
  detect: (source: CanvasImageSource) => Promise<Array<{ rawValue: string }>>;
}

interface BarcodeDetectorCtor {
  new (options?: { formats?: string[] }): BarcodeDetectorLike;
  getSupportedFormats?: () => Promise<string[]>;
}

function getDetectorCtor(): BarcodeDetectorCtor | undefined {
  return (globalThis as { BarcodeDetector?: BarcodeDetectorCtor }).BarcodeDetector;
}

export function cameraSupported(): boolean {
  return !!getDetectorCtor() && !!navigator.mediaDevices?.getUserMedia;
}

/**
 * Sürekli açık kamera paneli.
 *
 * Ekranın üst kısmında sabit durur ve okumadan sonra kapanmaz: iki barkodu
 * arka arkaya okutmak için kamerayı iki kez açıp kapatmak akışı kesiyordu.
 * Eski projede de kamera sabit bir panelde sürekli açıktı (rabiekle.dart:288).
 *
 * Fiziksel okuyucusu olmayan telefonlar için. El terminallerinde bu panel
 * hiç gösterilmez — lazer okuyucu her zaman daha hızlı.
 */
export function CameraScanner({ onScan, onClose }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [hazir, setHazir] = useState(false);

  /**
   * Kamera sürekli açık olduğu için aynı barkod saniyede onlarca kez
   * okunur. Aynı kodun tekrarı kısa süre boyunca yok sayılır; farklı bir
   * kod okununca bekleme sıfırlanır, böylece iki barkod hızla arka arkaya
   * okutulabilir.
   */
  const sonKod = useRef<{ kod: string; zaman: number } | null>(null);

  useEffect(() => {
    const ctor = getDetectorCtor();
    if (!ctor) {
      setHata('Bu cihazın tarayıcısı kamera ile barkod okumayı desteklemiyor.');
      return;
    }

    let stream: MediaStream | null = null;
    let durduruldu = false;
    let kare = 0;

    const detector = new ctor({
      formats: [
        'ean_13',
        'ean_8',
        'code_128',
        'code_39',
        'code_93',
        'itf',
        'upc_a',
        'upc_e',
        'qr_code',
        'data_matrix',
      ],
    });

    (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: {
            // Arka kamera; el terminali/telefon fark etmeksizin doğru olan bu.
            facingMode: { ideal: 'environment' },
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
        });

        if (durduruldu) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }

        const video = videoRef.current;
        if (!video) return;

        video.srcObject = stream;
        await video.play();
        setHazir(true);

        const tara = async () => {
          if (durduruldu) return;

          /**
           * Her karede çözümleme yapmak gereksiz CPU harcar ve ucuz
           * cihazlarda görüntüyü takar. Üç karede bir yeterli.
           */
          if (++kare % 3 === 0 && video.readyState >= 2) {
            try {
              const sonuc = await detector.detect(video);
              const kod = sonuc[0]?.rawValue?.trim();

              if (kod) {
                const simdi = performance.now();
                const onceki = sonKod.current;
                const tekrar =
                  onceki && onceki.kod === kod && simdi - onceki.zaman < 2500;

                if (!tekrar) {
                  sonKod.current = { kod, zaman: simdi };
                  onScan(kod);
                } else {
                  // Bekleme süresini uzatma; kod kadrajdan çıkınca sıfırlanır.
                  onceki.zaman = simdi;
                }
              }
            } catch {
              // Tek bir karenin çözülememesi normaldir; taramaya devam.
            }
          }

          requestAnimationFrame(() => void tara());
        };

        void tara();
      } catch (err) {
        if (durduruldu) return;
        const ad = err instanceof Error ? err.name : '';
        setHata(
          ad === 'NotAllowedError'
            ? 'Kamera izni verilmedi. Cihaz ayarlarından izin vermeniz gerekiyor.'
            : 'Kamera açılamadı.',
        );
      }
    })();

    return () => {
      durduruldu = true;
      stream?.getTracks().forEach((t) => t.stop());
    };
  }, [onScan]);

  if (hata) {
    return (
      <div className="kamera-panel hata">
        <span>{hata}</span>
        {onClose && (
          <button className="btn-kamera-kapat" onClick={onClose} aria-label="Kapat">
            ✕
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="kamera-panel">
      <video ref={videoRef} playsInline muted className="kamera-video" />
      <div className="kamera-cerceve" />

      {!hazir && <span className="kamera-bilgi">Kamera açılıyor…</span>}

      {onClose && (
        <button
          className="btn-kamera-kapat ustte"
          onClick={onClose}
          aria-label="Kamerayı kapat"
        >
          ✕
        </button>
      )}
    </div>
  );
}
